import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { Eye, EyeOff, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { friendlyAuthError } from "@/lib/auth-errors";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { trackFunnel } from "@/lib/funnel";
import { ACCOUNT_CHOICES, persistAccountChoice, type AccountChoice } from "@/lib/account-choices";
// Deferred: the strength meter only matters once the user starts typing a
// password, and the inbox panel only after submit — keeping both out of the
// signup entry chunk shortens the critical path to first paint.
const PasswordStrengthMeter = lazy(() =>
  import("@/components/PasswordStrengthMeter").then((m) => ({ default: m.PasswordStrengthMeter })),
);
import { scorePassword } from "@/lib/password-strength";
import { usePwnedPassword } from "@/lib/pwned-password";
const CheckInboxPanel = lazy(() =>
  import("@/components/CheckInboxPanel").then((m) => ({ default: m.CheckInboxPanel })),
);



export const Route = createFileRoute("/signup")({
  component: SignupPage,
  // Accept an optional post-signup destination (used by invitation links) and a
  // prefilled email so invited users land back on their invite after signing up.
  validateSearch: (s: Record<string, unknown>): { next?: string; email?: string; plan?: string; interval?: "month" | "year" } => {
    const raw = typeof s.next === "string" ? s.next : (typeof s.redirect === "string" ? s.redirect : undefined);
    let next = raw;
    if (raw && raw.startsWith("%2F")) {
      try { next = decodeURIComponent(raw); } catch { next = raw; }
    }
    return {
      next: next && next.startsWith("/") && !next.startsWith("//") ? next : undefined,
      email: typeof s.email === "string" ? s.email : undefined,
      plan: typeof s.plan === "string" && ["free", "solo", "starter", "pro", "enterprise"].includes(s.plan) ? s.plan : undefined,
      interval: s.interval === "year" || s.interval === "month" ? s.interval : undefined,
    };
  },

  head: () => ({
    meta: [
      { title: "Start free trial — MissionReady" },
      { name: "description", content: "Start a 30-day free MissionReady trial. Stand up your security operations center in minutes — no credit card required." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Start free trial — MissionReady" },
      { property: "og:description", content: "Stand up your security operations center in minutes. 30-day free trial, no credit card required." },
      { property: "og:url", content: "https://missionready.dev/signup" },
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/signup" }],
  }),
});

// Permissive but useful email shape check. Server is still source of truth.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type FormError =
  | { kind: "duplicate"; email: string }
  | { kind: "rate_limit"; seconds: number }
  | { kind: "weak_password"; message: string }
  | { kind: "network" }
  | { kind: "generic"; message: string };

function SignupPage() {
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const { next, email: emailFromQuery, plan: planFromQuery, interval: intervalFromQuery } = Route.useSearch();
  const afterAuth = next && next.startsWith("/") && !next.startsWith("//") ? next : "/onboarding";

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState(() => (emailFromQuery ?? "").trim().toLowerCase());

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [choice, setChoice] = useState<AccountChoice["value"]>(() =>
    planFromQuery === "free" ? "individual" : planFromQuery === "solo" ? "solo" : "agency",
  );

  // Per-field "touched" state so we don't yell at users before they've typed.
  const [touched, setTouched] = useState<{ name?: boolean; email?: boolean; pw?: boolean; confirm?: boolean }>({});

  const [submitting, setSubmitting] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const [checkInboxFor, setCheckInboxFor] = useState<string | null>(null);
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState<FormError | null>(null);

  const pwStrength = useMemo(() => scorePassword(password), [password]);
  const pwned = usePwnedPassword(password);
  const emailValid = EMAIL_RE.test(email.trim());
  const nameValid = fullName.trim().length >= 2;
  const passwordsMatch = confirmPassword === "" || confirmPassword === password;
  const rateLimitRemaining = rateLimitUntil ? Math.max(0, Math.ceil((rateLimitUntil - now) / 1000)) : 0;

  // Per-field inline messages — only shown after the user has touched the field.
  const nameInlineError = touched.name && !nameValid ? "Please enter your full name." : null;
  const emailInlineError =
    touched.email && email.length > 0 && !emailValid
      ? "That doesn't look like a valid email address."
      : null;
  const pwInlineError =
    password.length > 0 && pwned.breached
      ? "This password appeared in a known data breach — please pick a different one."
      : touched.pw && password.length > 0 && !pwStrength.acceptable
        ? (pwStrength.issues[0] ?? "Please choose a stronger password.")
        : null;
  const confirmInlineError =
    touched.confirm && confirmPassword.length > 0 && !passwordsMatch
      ? "Passwords do not match."
      : null;


  // Tick once a second while a rate-limit countdown is active.
  useEffect(() => {
    if (!rateLimitUntil) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [rateLimitUntil]);

  // Clear a stale rate-limit banner once the countdown hits zero.
  useEffect(() => {
    if (formError?.kind === "rate_limit" && rateLimitRemaining === 0) setFormError(null);
  }, [formError, rateLimitRemaining]);

  const selected = ACCOUNT_CHOICES.find((c) => c.value === choice)!;

  useEffect(() => {
    trackFunnel("signup_page_viewed", {}, { once: true });
  }, []);

  useEffect(() => {
    if (!loading && session) navigate({ to: afterAuth as never });
  }, [session, loading, navigate, afterAuth]);

  // Persist the chosen account-path + plan so /onboarding (and the Google round-trip)
  // can pick up the user's choice even after an email-confirm redirect.
  useEffect(() => {
    persistAccountChoice(selected, selected.value === "agency" || selected.value === "solo" ? planFromQuery : undefined, intervalFromQuery);
  }, [selected]);

  const markStarted = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackFunnel("signup_form_started");
    }
  };

  // Auto-normalize email: strip whitespace and lowercase as the user types.
  // Stops "  Jane@Foo.com " → "Jane@Foo.com " duplicate-account false negatives.
  const onEmailChange = (raw: string) => {
    markStarted();
    setEmail(raw.replace(/\s+/g, "").toLowerCase());
    // Clear duplicate-account banner the moment the user starts editing.
    if (formError?.kind === "duplicate") setFormError(null);
  };

  const formInvalid =
    !nameValid ||
    !emailValid ||
    !pwStrength.acceptable ||
    pwned.breached ||
    !passwordsMatch ||
    confirmPassword.length === 0;


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Mark everything touched so any remaining inline errors surface.
    setTouched({ name: true, email: true, pw: true, confirm: true });

    if (rateLimitRemaining > 0) {
      setFormError({ kind: "rate_limit", seconds: rateLimitRemaining });
      return;
    }
    if (!nameValid || !emailValid) {
      setFormError({ kind: "generic", message: "Please fix the highlighted fields and try again." });
      return;
    }
    if (password !== confirmPassword) {
      trackFunnel("signup_failed", { reason: "client_passwords_mismatch" });
      setFormError({ kind: "generic", message: "Passwords do not match." });
      return;
    }
    if (!pwStrength.acceptable) {
      trackFunnel("signup_failed", { reason: "client_weak_password", score: pwStrength.score });
      setFormError({
        kind: "weak_password",
        message: pwStrength.issues[0] ?? "Please choose a stronger password.",
      });
      return;
    }
    if (pwned.breached) {
      trackFunnel("signup_failed", { reason: "client_breached_password", count: pwned.count });
      setFormError({
        kind: "weak_password",
        message: "This password appeared in a known data breach. Please choose a different one.",
      });
      return;
    }


    setFormError(null);
    setSubmitting(true);
    const redirectTo = typeof window !== "undefined" ? window.location.origin + afterAuth : undefined;
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: { full_name: fullName.trim(), account_type: selected.path, plan_preselect: selected.planKey },
        emailRedirectTo: redirectTo,
      },
    });
    setSubmitting(false);

    if (error) {
      trackFunnel("signup_failed", { reason: error.message });
      const msg = error.message.toLowerCase();

      // Rate limit — show a visible countdown instead of the raw "after N seconds" string.
      const secMatch = /after (\d+) seconds?/i.exec(error.message);
      if (secMatch || msg.includes("rate limit") || msg.includes("too many")) {
        const seconds = secMatch ? Number(secMatch[1]) : 30;
        setRateLimitUntil(Date.now() + seconds * 1000);
        setNow(Date.now());
        setFormError({ kind: "rate_limit", seconds });
        return;
      }

      // Duplicate account — most common drop-off. Offer a one-click path to /login.
      if (msg.includes("already registered") || msg.includes("already exists") || msg.includes("user already")) {
        setFormError({ kind: "duplicate", email: email.trim() });
        return;
      }

      // Weak / breached password
      if (msg.includes("weak password") || msg.includes("pwned") || (msg.includes("password") && msg.includes("should be"))) {
        setFormError({ kind: "weak_password", message: friendlyAuthError(error) });
        return;
      }

      // Network
      if (msg.includes("network") || msg.includes("failed to fetch")) {
        setFormError({ kind: "network" });
        return;
      }

      setFormError({ kind: "generic", message: friendlyAuthError(error) });
      return;
    }

    // NOTE: signup_completed is fired centrally by the auth listener via
    // maybeTrackSignupCompleted() so it also catches Google OAuth round-trips
    // and email-confirm redirects. We only attach the chosen plan/account
    // metadata here so the centralized event picks it up via localStorage.
    try {
      localStorage.setItem("mr_signup_meta", JSON.stringify({
        account_type: selected.path,
        plan_preselect: selected.planKey,
        has_session: !!data.session,
      }));
    } catch { /* ignore */ }

    if (data.session) {
      toast.success("Account created");
    } else {
      setCheckInboxFor(email.trim());
    }
  };

  return (
    <main className="flex min-h-dvh items-center justify-center bg-hero px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-card">
          {checkInboxFor ? (
            <Suspense fallback={<div className="h-40" aria-hidden />}>
            <CheckInboxPanel
              email={checkInboxFor}
              onUseDifferentEmail={() => setCheckInboxFor(null)}
              redirectTo={afterAuth}
            />
            </Suspense>
          ) : (

          <>
          <h1 className="font-display text-2xl font-semibold">Start your 30-day trial</h1>
          <p className="mt-1 text-sm text-muted-foreground">No credit card required. Takes under a minute.</p>

          {/* Primary CTA: Google — one-tap signup, pre-fills email automatically. */}
          <div className="mt-6"><GoogleSignInButton label="Sign up with Google" redirectTo={afterAuth} /></div>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or sign up with email <div className="h-px flex-1 bg-border" />
          </div>

          {/* Form-level error banner. Stays visible until resolved, unlike a toast. */}
          {formError && (
            <Alert variant="destructive" className="mb-4">
              {formError.kind === "rate_limit" ? <Clock className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              <AlertTitle>
                {formError.kind === "duplicate" && "Account already exists"}
                {formError.kind === "rate_limit" && "Too many attempts"}
                {formError.kind === "weak_password" && "Choose a stronger password"}
                {formError.kind === "network" && "Connection problem"}
                {formError.kind === "generic" && "We couldn't create your account"}
              </AlertTitle>
              <AlertDescription>
                {formError.kind === "duplicate" && (
                  <>
                    An account with <span className="font-medium">{formError.email}</span> already exists.{" "}
                    <Link
                      to="/login"
                      search={{ email: formError.email } as never}
                      className="font-medium underline underline-offset-2"
                    >
                      Sign in instead
                    </Link>.
                  </>
                )}
                {formError.kind === "rate_limit" && (
                  <>Please wait <span className="font-medium">{rateLimitRemaining}s</span> before trying again.</>
                )}
                {formError.kind === "weak_password" && <>{formError.message}</>}
                {formError.kind === "network" && <>Can't reach the server. Check your connection and try again.</>}
                {formError.kind === "generic" && <>{formError.message}</>}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} onFocus={markStarted} className="space-y-4" autoComplete="on" noValidate>
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                name="name"
                autoComplete="name"
                required
                value={fullName}
                onChange={(e) => { markStarted(); setFullName(e.target.value); }}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                className={`mt-1 ${nameInlineError ? "border-destructive focus-visible:ring-destructive/40" : ""}`}
                aria-invalid={!!nameInlineError}
                aria-describedby={nameInlineError ? "name-err" : undefined}
              />
              {nameInlineError && <p id="name-err" className="mt-1 text-xs text-destructive">{nameInlineError}</p>}
            </div>

            <div>
              <Label htmlFor="email">Work email</Label>
              <div className="relative mt-1">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  className={`pr-9 ${emailInlineError ? "border-destructive focus-visible:ring-destructive/40" : ""}`}
                  aria-invalid={!!emailInlineError}
                  aria-describedby={emailInlineError ? "email-err" : undefined}
                />
                {emailValid && (
                  <CheckCircle2
                    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-500"
                    size={18}
                    aria-hidden="true"
                  />
                )}
              </div>
              {emailInlineError && <p id="email-err" className="mt-1 text-xs text-destructive">{emailInlineError}</p>}
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => { markStarted(); setPassword(e.target.value); }}
                  onBlur={() => setTouched((t) => ({ ...t, pw: true }))}
                  className={`pr-10 ${pwInlineError ? "border-destructive focus-visible:ring-destructive/40" : ""}`}
                  aria-invalid={!!pwInlineError}
                  aria-describedby={pwInlineError ? "pw-err" : undefined}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm p-0.5"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {password.length > 0 && (
                <Suspense fallback={<div className="mt-2 h-6" aria-hidden />}>
                  <PasswordStrengthMeter password={password} />
                </Suspense>
              )}
              {pwInlineError ? (
                <p id="pw-err" className="mt-1.5 text-xs font-medium text-destructive">{pwInlineError}</p>
              ) : pwned.checking ? (
                <p className="mt-1.5 text-xs text-muted-foreground">Checking against known breaches…</p>
              ) : null}

            </div>

            <div>
              <Label htmlFor="confirm-password">Confirm password</Label>
              <div className="relative mt-1">
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => { markStarted(); setConfirmPassword(e.target.value); }}
                  onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
                  className={`pr-10 ${confirmInlineError ? "border-destructive focus-visible:ring-destructive/40" : ""}`}
                  aria-invalid={!!confirmInlineError}
                  aria-describedby={confirmInlineError ? "confirm-err" : undefined}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm p-0.5"
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {confirmInlineError && <p id="confirm-err" className="mt-1 text-xs text-destructive">{confirmInlineError}</p>}
            </div>

            <div>
              <Label>I'm signing up as</Label>
              <div className="mt-2 grid gap-2">
                {ACCOUNT_CHOICES.map((opt) => {
                  const active = choice === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${
                        active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                      }`}
                    >
                      <input
                        type="radio"
                        name="account_type"
                        value={opt.value}
                        checked={active}
                        onChange={() => { markStarted(); setChoice(opt.value); }}
                        className="mt-1 h-4 w-4 accent-primary"
                      />
                      <div className="flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="text-sm font-medium text-foreground">{opt.title}</div>
                          <div className="text-right">
                            <div className="font-display text-sm font-semibold text-foreground">{opt.price}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{opt.priceNote}</div>
                          </div>
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{opt.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                {choice === "agency" && planFromQuery && ["starter", "pro", "enterprise"].includes(planFromQuery) && (
                  <span className="block font-medium text-foreground">
                    Selected: {planFromQuery === "pro" ? "Pro Org" : planFromQuery === "enterprise" ? "Enterprise" : "Starter Org"}
                    {intervalFromQuery === "year" ? " · billed yearly" : " · billed monthly"} — confirmed in setup.
                  </span>
                )}
                You can change plans any time from Billing. Trials never auto-charge — you'll be asked first.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting || rateLimitRemaining > 0 || (formStarted && formInvalid)}
            >
              {submitting
                ? "Creating…"
                : rateLimitRemaining > 0
                  ? `Try again in ${rateLimitRemaining}s`
                  : "Create account"}
            </Button>
            {rateLimitRemaining > 0 && (
              <p className="text-center text-xs text-muted-foreground">
                The button will re-enable automatically in {rateLimitRemaining}s.
              </p>
            )}
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary underline underline-offset-2">Sign in</Link>
          </p>
          </>
          )}
        </div>
      </div>
    </main>
  );
}
